import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Divider } from '@mui/material';

// Service list components
export const ServiceRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
}));

export const ServiceInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
}));

export const ActionButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));


// Progress timeline components
export const ProgressDot = styled('div')(({ theme, completed }) => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: completed ? theme.palette.primary.main : theme.palette.grey[300],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

export const ProgressLine = styled('div')(({ theme, completed }) => ({
    width: 2,
    height: 40,
    backgroundColor: completed ? theme.palette.primary.main : theme.palette.grey[300],
    margin: theme.spacing(0.5, 0),
    marginLeft: 11,
}));

export const ExpandedSection = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
}));

export const MainContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    minHeight: 'min-content',
    maxHeight: '80vh',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

export const LeftColumn = styled(Box)(({ theme }) => ({
    width: '280px',
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        borderRight: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

export const RightColumn = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(2),
    overflowX: 'hidden',
}));

export const TabButton = styled(Button)(({ theme, selected }) => ({
    height: '28px',
    minWidth: '90px',
    borderRadius: '100px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'none',
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    '&:hover': {
        backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
    },
    marginRight: theme.spacing(1),
}));

export const ServiceHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        '& > div': {
            marginBottom: theme.spacing(1.5),
        },
    },
}));

export const CompanyLogo = styled(Box)(({ theme }) => ({
    width: 100,
    height: 100,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
}));

export const ServiceTitle = styled(Typography)(({ theme }) => ({
    ...theme.typography.h5,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.2,
}));

export const LocationText = styled(Typography)(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1),
}));

export const SocialIcons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

// Progress timeline components
export const ProgressContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(3),
}));

export const ProgressStep = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
}));

export const ProgressContent = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(0.5),
}));

export const CompanyInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
}));

export const VenueImageContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    height: 200,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
}));

export const VenueActionButton = styled(Button)(({ theme, primary }) => ({
    ...theme.typography.button,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 0),
    textTransform: 'none',
    width: '100%',
    marginBottom: theme.spacing(1),
    ...(primary && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    }),
    ...(!primary && {
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
    }),
}));

// Form components
export const FormRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
        borderBottom: 'none',
    },
}));

export const ActionButton = styled(Button)(({ theme, variant }) => ({
    ...theme.typography.button,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 1.5),
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    gap: theme.spacing(1),
    ...(variant === 'download' && {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
    }),
    ...(variant === 'submit' && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    }),
}));

export const StatusBadge = styled(Box)(({ theme, status }) => ({
    ...theme.typography.caption,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    ...(status === 'completed' && {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    }),
    ...(status === 'awaiting' && {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
    }),
}));

// Invoice components
export const InvoiceContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

export const InvoiceHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

export const InvoiceRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
}));

export const TotalRow = styled(InvoiceRow)(({ theme }) => ({
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
}));

export const AmountText = styled(Typography)(({ theme, color }) => ({
    ...theme.typography.body1,
    fontWeight: 500,
    ...(color === 'positive' && {
        color: theme.palette.success.main,
    }),
    ...(color === 'negative' && {
        color: theme.palette.error.main,
    }),
}));

export const ImageCarousel = styled(Box)(({ theme }) => ({
    width: '100%',
    height: 300,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    position: 'relative',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
}));

export const CarouselNav = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: theme.spacing(0.5),
}));

export const CarouselDot = styled('div')(({ active }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: active ? '#fff' : 'rgba(255, 255, 255, 0.7)',
    opacity: active ? 1 : 0.7,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
}));

export const CapacityTag = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
    width: 'fit-content',
}));

// Venue statistic box
export const VenueStat = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
}));

export const MapContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    height: 200,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    '& iframe': {
        width: '100%',
        height: '100%',
        border: 'none',
    },
})); 